import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isNativeApp, getPlatform, NATIVE_HOME_ROUTE } from "@/lib/native/platform";
import { initNativeShell } from "@/lib/native/shell";
import { initNativeFileInputBridge } from "@/lib/native/fileInputBridge";
import { NativeBiometricLock } from "./NativeBiometricLock";
import { NativeFilePickerSheet } from "./NativeFilePickerSheet";
import { NativePushBoot } from "./NativePushBoot";
import { NativeOfflineBoot } from "./NativeOfflineBoot";
import { NativeUpdatePrompt } from "./NativeUpdatePrompt";
import { NativeTelemetryBoot } from "./NativeTelemetryBoot";


/**
 * Native shell bootstrap.
 *
 * On the Capacitor iOS/Android build this:
 *  - marks <html> with `data-native` + `data-native-platform` so CSS can apply
 *    safe-area padding only inside the native shell,
 *  - redirects the marketing entry points to HIBANE Desk (/admin) so the app
 *    opens directly into the Desk (auth is handled by AdminProtectedRoute),
 *  - configures the status bar / splash screen / Android back button,
 *  - renders the biometric app lock when the user enabled it.
 *
 * On the web it renders nothing and changes nothing.
 */
export const NativeAppBoot = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp()) return;
    const root = document.documentElement;
    root.setAttribute("data-native", "true");
    root.setAttribute("data-native-platform", getPlatform());
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cleanup: (() => void) | undefined;
    void initNativeShell().then((fn) => {
      cleanup = fn;
    });
    return () => cleanup?.();
  }, []);

  // Route every <input type="file"> through the native camera / picker sheet.
  useEffect(() => {
    if (!isNativeApp()) return;
    return initNativeFileInputBridge();
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;
    const path = location.pathname;
    // Only bounce the marketing shell root paths — deep links into other
    // portals (/voice, /socials, /welcome/:slug) keep working.
    const isMarketingEntry = path === "/" || path === "/home";
    if (isMarketingEntry) {
      navigate(NATIVE_HOME_ROUTE, { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <>
      <NativeBiometricLock />
      <NativeFilePickerSheet />
      <NativePushBoot />
      <NativeOfflineBoot />
      <NativeUpdatePrompt />
      <NativeTelemetryBoot />
    </>
  );
};


export default NativeAppBoot;

